import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, collectionGroup, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { companyName, errorMessage, formatDate, formatMoney, startOfMonth } from '../lib/format';

function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        // Solo las ventas del mes: antes se descargaba el historial completo
        // de todas las tiendas para quedarse con un mes.
        const monthSales = query(
          collectionGroup(db, 'sales'),
          where('timestamp', '>=', startOfMonth()),
          orderBy('timestamp', 'desc'),
        );
        const [companiesSnap, salesSnap, usersSnap] = await Promise.all([
          getDocs(collection(db, 'companies')),
          getDocs(monthSales),
          getDocs(collectionGroup(db, 'users')),
        ]);

        const salesBy = {};
        salesSnap.docs.forEach((d) => {
          const id = d.ref.parent.parent?.id;
          const sale = d.data();
          salesBy[id] = salesBy[id] || { total: 0, count: 0 };
          salesBy[id].total += sale.totalAmount || 0;
          salesBy[id].count += 1;
        });

        const usersBy = {};
        usersSnap.docs.forEach((d) => {
          const id = d.ref.parent.parent?.id;
          usersBy[id] = (usersBy[id] || 0) + 1;
        });

        const comps = companiesSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          monthSales: salesBy[d.id]?.total || 0,
          monthCount: salesBy[d.id]?.count || 0,
          usersCount: usersBy[d.id] || 0,
        }));
        comps.sort((a, b) => b.monthSales - a.monthSales || companyName(a).localeCompare(companyName(b)));
        setCompanies(comps);
      } catch (err) {
        console.error('Error fetching companies:', err);
        setError(errorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  const term = search.trim().toLowerCase();
  const visible = term
    ? companies.filter((c) =>
        [companyName(c), c.companyCode, c.id, c.rif, c.phone].some((v) => String(v || '').toLowerCase().includes(term)))
    : companies;

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tiendas</h1>
          <p className="page-subtitle">{companies.length} tiendas registradas en Estoquia</p>
        </div>
        <input
          type="search"
          className="input-field search-field"
          placeholder="Buscar por nombre, código o RIF"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className="notice notice-error">No se pudieron cargar las tiendas. {error}</div>}

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div className="empty-state"><span className="spinner spinner-dark" /></div>
          ) : visible.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🏬</div>
              <p>{term ? 'Ninguna tienda coincide con la búsqueda' : 'No hay tiendas registradas'}</p>
            </div>
          ) : (
            <table className="table-clickable">
              <thead>
                <tr>
                  <th>Tienda</th>
                  <th>Código</th>
                  <th>RIF</th>
                  <th className="num">Usuarios</th>
                  <th className="num">Ventas del mes</th>
                  <th>Alta</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((company) => (
                  <tr
                    key={company.id}
                    tabIndex={0}
                    onClick={() => navigate(`/companies/${company.id}`)}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/companies/${company.id}`)}
                  >
                    <td>
                      <div className="font-semibold">{companyName(company)}</div>
                      <div className="mono-text">{company.id}</div>
                    </td>
                    <td className="mono-text">{company.companyCode || '—'}</td>
                    <td>{company.rif || '—'}</td>
                    <td className="num">{company.usersCount}</td>
                    <td className="num">
                      <div className="text-success font-bold">{formatMoney(company.monthSales, company.currency)}</div>
                      <div className="text-muted text-xs">{company.monthCount} ventas</div>
                    </td>
                    <td className="text-secondary">{formatDate(company.createdAt, false)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default Companies;
